import { IColor, IVector3 } from '../api.js';
import { LightSystem } from './light-manager/light-system/LightSystem.js';
import { ViewerMaterialWrapper } from './material/ViewerMaterialWrapper.js';
import { BetraktaPBRMaterial } from './material/BetraktaPBRMaterial.js';
import { Logger } from '../logging/Logger.js';
const LOGGER_CATEGORY = "Material";
export function mark_material_rendered(material, rendered = true) {
    material.metadata = material.metadata || {};
    material.metadata.rendered = rendered;
}
export function is_material_rendered(material) {
    return material.metadata && material.metadata.rendered;
}
export class Material {
    constructor(viewer, material_id, texture_manager, freezing_manager, material) {
        this.freezing_manager = freezing_manager;
        this._material = null;
        this.is_default_created_material = false;
        this._material_url = null;
        this._custom_base_color = null;
        this._original_base_color = new IColor(1.0, 1.0, 1.0);
        this._custom_emissive_color = null;
        this._original_emissive_color = new IColor(0, 0, 0);
        this._custom_emissive_intensity = -1;
        this._original_emissive_intensity = 0.5;
        this._custom_roughness = null;
        this._original_roughness = 1.0;
        this._custom_metallic = null;
        this._original_metallic = 1.0;
        this._custom_base_color_texture = null;
        this._original_base_color_texture = null;
        this._custom_normal_texture = null;
        this._original_normal_texture = null;
        this._custom_occlusion_roughness_metallic_texture = null;
        this._original_occlusion_roughness_metallic_texture = null;
        this._custom_lightmap_texture = null;
        this._original_lightmap_texture = null;
        this.ready = Promise.resolve();
        this._viewer = viewer;
        this.material_id = material_id;
        this._texture_manager = texture_manager;
        if (material instanceof ViewerMaterialWrapper) {
            material.babylon_material.maxSimultaneousLights = LightSystem.BABYLON_MATERIAL_MAXIMUM_LIGHT_COUNT;
            this._material = material;
        }
        else if (material instanceof BetraktaPBRMaterial) {
            this._material = material;
        }
        else if (material !== undefined) {
            throw new Error('Invalid material base class.');
        }
    }
    get material() {
        return this._material;
    }
    set material(material) {
        if (material === this._material) {
            return;
        }
        if (this._material) {
            this._material.dispose();
        }
        this._material = material === null ? null : material;
        if (this._material !== null && material !== null) {
            // Store original material values
            this._original_metallic = material.metallic === null ? 1.0 : material.metallic;
            this._original_roughness = material.roughness === null ? 1.0 : material.roughness;
            this._original_base_color = IColor.to_sRGB(material.base_color_factor);
            this._original_emissive_color = IColor.to_sRGB(material.emissive_factor);
            this._original_emissive_intensity = material.emissive_strength;
            this._original_base_color_texture = material.base_color_texture;
            this._original_normal_texture = material.normal_texture;
            this._original_occlusion_roughness_metallic_texture = material.occlusion_roughness_metallic_texture;
            this._original_lightmap_texture = material.lightmap_texture;
            // Apply custom values, if set
            if (this._custom_base_color !== null) {
                IVector3.copy_to_ref(IColor.to_linear_space(this._custom_base_color), this._material.base_color_factor);
            }
            if (this._custom_metallic !== null) {
                material.metallic = this._custom_metallic;
            }
            if (this._custom_roughness !== null) {
                material.roughness = this._custom_roughness;
            }
            if (this._custom_base_color_texture !== null) {
                material.base_color_texture = this._custom_base_color_texture;
            }
            if (this._custom_emissive_color !== null) {
                IVector3.copy_to_ref(IColor.to_linear_space(this._custom_emissive_color), this._material.emissive_factor);
            }
            if (this._custom_emissive_intensity !== -1) {
                material.emissive_strength = this._custom_emissive_intensity;
            }
            if (this._custom_normal_texture !== null) {
                material.normal_texture = this._custom_normal_texture;
            }
            if (this._custom_occlusion_roughness_metallic_texture !== null) {
                material.occlusion_roughness_metallic_texture = this._custom_occlusion_roughness_metallic_texture;
            }
            if (this._custom_lightmap_texture !== null) {
                material.lightmap_texture = this._custom_lightmap_texture;
            }
        }
        this.set_dirty();
    }
    get roughness() {
        return this._custom_roughness !== null ? this._custom_roughness : this._original_roughness;
    }
    set roughness(v) {
        this._custom_roughness = v;
        if (this._material) {
            this._material.roughness = v;
        }
        this.set_dirty();
    }
    reset_roughness() {
        this._custom_roughness = null;
        if (this._material) {
            this._material.roughness = this._original_roughness;
        }
        this.set_dirty();
    }
    get metallic() {
        return this._custom_metallic !== null ? this._custom_metallic : this._original_metallic;
    }
    set metallic(v) {
        this._custom_metallic = v;
        if (this._material) {
            this._material.metallic = v;
        }
        this.set_dirty();
    }
    reset_metallic() {
        this._custom_metallic = null;
        if (this._material) {
            this._material.metallic = this._original_metallic;
        }
        this.set_dirty();
    }
    get base_color() {
        return this._custom_base_color !== null ? this._custom_base_color : this._original_base_color;
    }
    set base_color(v) {
        this._custom_base_color = v;
        if (this._material) {
            IVector3.copy_to_ref(IColor.to_linear_space(v), this._material.base_color_factor);
        }
        this.set_dirty();
    }
    get emissive_color() {
        return this._custom_emissive_color !== null ? this._custom_emissive_color : this._original_emissive_color;
    }
    set emissive_color(v) {
        this._custom_emissive_color = IColor.copy(v);
        if (this._material) {
            IVector3.copy_to_ref(IColor.to_linear_space(this._custom_emissive_color), this._material.emissive_factor);
        }
        this.set_dirty();
    }
    reset_emissive_color() {
        this._custom_emissive_color = null;
        if (this._material) {
            IVector3.copy_to_ref(IColor.to_linear_space(this._original_emissive_color), this._material.emissive_factor);
        }
        this.set_dirty();
    }
    set emissive_intensity(v) {
        this._custom_emissive_intensity = v;
        if (this._material) {
            this._material.emissive_strength = v;
        }
        this.set_dirty();
    }
    reset_emissive_intensity(v) {
        this._custom_emissive_intensity = -1;
        if (this._material) {
            this._material.emissive_strength = this._original_emissive_intensity;
        }
        this.set_dirty();
    }
    reset_base_color() {
        this._custom_base_color = null;
        if (this._material) {
            IVector3.copy_to_ref(IColor.to_linear_space(this._original_base_color), this._material.base_color_factor);
        }
        this.set_dirty();
    }
    get base_color_texture() {
        return this._custom_base_color_texture !== null ? this._custom_base_color_texture : this._original_base_color_texture;
    }
    set base_color_texture(v) {
        this._custom_base_color_texture = v;
        if (this._material) {
            this.set_texture('albedoTexture', v);
        }
    }
    reset_base_color_texture() {
        this._custom_base_color_texture = null;
        if (this.material) {
            this.set_texture('albedoTexture', this._original_base_color_texture);
        }
    }
    get normal_texture() {
        return this._custom_normal_texture !== null ? this._custom_normal_texture : this._original_normal_texture;
    }
    set normal_texture(v) {
        this._custom_normal_texture = v;
        if (this.material) {
            this.set_texture('bumpTexture', v);
        }
    }
    reset_normal_texture() {
        this._custom_normal_texture = null;
        if (this.material) {
            this.set_texture('bumpTexture', this._original_normal_texture);
        }
    }
    get occlusion_roughness_metallic_texture() {
        return this._custom_occlusion_roughness_metallic_texture !== null
            ? this._custom_occlusion_roughness_metallic_texture
            : this._original_occlusion_roughness_metallic_texture;
    }
    set occlusion_roughness_metallic_texture(v) {
        this._custom_occlusion_roughness_metallic_texture = v;
        if (this.material) {
            this.set_texture('metallicTexture', v);
        }
    }
    reset_occlusion_roughness_metallic_texture() {
        this._custom_occlusion_roughness_metallic_texture = null;
        if (this.material) {
            this.set_texture('metallicTexture', this._original_occlusion_roughness_metallic_texture);
        }
    }
    get lightmap_texture() {
        return this._custom_lightmap_texture !== null ? this._custom_lightmap_texture : this._original_lightmap_texture;
    }
    set lightmap_texture(v) {
        this._custom_lightmap_texture = v;
        if (this.material) {
            this.set_texture('lightmapTexture', v);
        }
    }
    get unlit() {
        return this.material.unlit;
    }
    set unlit(unlit) {
        this.material.unlit = unlit;
    }
    set_opacity(alpha) {
        if (this.material) {
            this.material.opacity = alpha;
        }
    }
    reset_lightmap_texture() {
        this._custom_lightmap_texture = null;
        if (this.material) {
            this.set_texture('lightmapTexture', this._original_lightmap_texture);
        }
    }
    set_texture(texture_property, id) {
        if (this.material) {
            // TODO: It is super shady using the bracket operator here, and does not work at all for BetraktaPBRMaterial
            this.material.babylon_material[texture_property] = (id !== null) ? this._texture_manager.get(id) : null;
            this.set_dirty();
        }
    }
    set_dirty() {
        if (this.material) {
            this.freezing_manager.set_material_dirty(this.material.babylon_material);
        }
    }
    dispose() {
        this.ready.then(() => {
            if (this._material) {
                this._material.dispose();
                this._material = null;
            }
        }, () => {
            Logger.as(LOGGER_CATEGORY).warn('Material is not ready to be disposed.');
        });
    }
}
//# sourceMappingURL=Material.js.map